import { useEffect, useState } from 'react'
import type { AddressBookEntrySourceMessage } from '../types'
import type { DeviceLocation } from '@/location/device-current-location/types'
import type { AddressSearchSourceValues } from '@/tools/analytics/events/domains/address-book'
import { useErrorHandling } from '@/tools/o11y/client'
import { AddressFlowLandingScreenEvent } from '@/user/address-book/analytics'

type Props = {
  setEntrySource: (entrySource: AddressSearchSourceValues) => void
  entrySource: AddressSearchSourceValues
  deviceLocation: DeviceLocation
}

const isAddressBookEntrySourceMessage = (
  message: unknown
): message is AddressBookEntrySourceMessage =>
  typeof message === 'object' &&
  message !== null &&
  'data' in message &&
  typeof message.data === 'object' &&
  message.data !== null &&
  'type' in message.data &&
  message.data.type === 'ADDRESS_BOOK.ENTRY_SOURCE'

/**
 *
 * TODO: This hook won't be needed once AddressBook is not consumed anymore from an Iframe
 *
 */
export function useEntrySource({ entrySource, setEntrySource, deviceLocation }: Props) {
  const [hasReceivedEntrySource, setHasReceivedEntrySource] = useState(false)
  const { execWithErrorTracking } = useErrorHandling()
  const isGeolocationPermissionGranted = deviceLocation.geolocationPermission === 'granted'

  function onParentMessage(message: unknown) {
    if (isAddressBookEntrySourceMessage(message)) {
      setEntrySource(message.data.payload)
      setHasReceivedEntrySource(true)
    }
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (window !== window.parent) {
        window.addEventListener('message', onParentMessage)
      }
      return () => {
        window.removeEventListener('message', onParentMessage)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    execWithErrorTracking(() => {
      if (hasReceivedEntrySource && isGeolocationPermissionGranted) {
        AddressFlowLandingScreenEvent(isGeolocationPermissionGranted, entrySource)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReceivedEntrySource, isGeolocationPermissionGranted])

  return {}
}
